import { useState } from 'react';
import { ImageUp, Loader2, X } from 'lucide-react';
import { parseBRL } from '@garlic/shared';
import { api, errorMessage } from '../lib/api';
import { Input, cx } from './primitives';
import { useToast } from './feedback';

/** Interruptor acessível (role=switch). */
export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50', checked ? 'bg-sprout-500' : 'bg-line')}
    >
      <span className={cx('inline-block size-5 rounded-full bg-white shadow transition-transform', checked ? 'translate-x-5' : 'translate-x-0.5')} />
    </button>
  );
}

/** Campo de valor em reais. Guarda texto ("12,50") e converte para centavos com parseBRL. */
export function MoneyInput({ id, value, onChange, invalid }: { id?: string; value: string; onChange: (v: string) => void; invalid?: boolean }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-ink-3">R$</span>
      <Input id={id} inputMode="decimal" className="pl-10 tabular-nums" value={value} invalid={invalid} onChange={(e) => onChange(e.target.value.replace(/[^\d,.]/g, ''))} placeholder="0,00" />
    </div>
  );
}
export const centsToInput = (c: number) => (c / 100).toFixed(2).replace('.', ',');
export const inputToCents = (s: string) => (s.trim() === '' ? 0 : parseBRL(s));

/** Envio de imagem do cardápio. */
export function ImageField({ value, onChange }: { value: string | null; onChange: (path: string | null) => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const upload = async (file: File) => {
    const body = new FormData();
    body.append('file', file);
    setBusy(true);
    try {
      const r = await api<{ path: string }>('/api/staff/uploads/image', { method: 'POST', body });
      onChange(r.path);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-xl border border-dashed border-line bg-paper">
        {value ? <img src={value} alt="" className="size-full object-cover" /> : <ImageUp className="size-5 text-ink-3" />}
      </div>
      <div className="flex flex-col gap-1.5">
        <label className={cx('inline-flex h-9 cursor-pointer items-center gap-2 rounded-xl border border-line bg-white px-3 text-sm font-medium hover:bg-paper-2', busy && 'pointer-events-none opacity-60')}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <ImageUp className="size-4" />} {value ? 'Trocar imagem' : 'Enviar imagem'}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {value && (
          <button type="button" onClick={() => onChange(null)} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-danger-600">
            <X className="size-3" /> Remover
          </button>
        )}
        <span className="text-xs text-ink-3">PNG, JPG ou WebP até 2 MB</span>
      </div>
    </div>
  );
}
